import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { Router } from '@angular/router';

import { ContentStoreDispatcher } from '@common/dispatchers/content.dispatcher';
import { MarketplaceStoreDispatcher } from '@common/dispatchers/marketplace.dispatcher';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { CONTENT_STORE_LOCAL_ACTIONS } from '@store/content/content.action';
import { DoodBlockModel } from '@core/models/block.model';
import {
  DoodMarketplaceModel,
  IBlockConfig,
  IMarketplaceUserInterface,
} from '@core/models/marketplace.model';

import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { OrderTypeService } from '@core/services/order-type/order-type.service';
import { ShopSearchParametersService } from '@core/services/shop-search-parameters/shop-search-parameters.service';

import { Cb2PreviewStateService } from './cb2-preview-state.service';
import { Cb2PreviewRuntimePayload, Cb2PreviewScenarioPayload } from './cb2-preview-protocol';
import { Cb2ContextualRuntimeResolverService } from '@core/services/content-builder/cb2-contextual-runtime-resolver.service';
import { ShopKeys } from '@config/keys/shop.keys';
import { JourneyParametersRuntimeService } from '@core/services/content-builder/journey-parameters-runtime.service';

import {
  applyMarketplaceUserInterface,
  convertToPageTree,
  injectCb2DefaultPages,
  injectDefaultModals,
  initializeDefaultDistributionMode,
  resolveAuthMode,
  setMarketplaceDefaultTimezone,
  setMarketplaceHtmlLang,
} from '../marketplace/marketplace-runtime.util';
import { createMarketplaceRoutes } from '../marketplace/marketplace-routes.util';
import { stabilizeInjectedPreviewModalBlockIds } from './stabilize-preview-modal-block-ids';

/**
 * Applies a CB2 preview marketplace stub to the webapp stores + UI + routes
 * without calling `getMarketplaceByDomain`. Reuses the standalone marketplace
 * runtime helpers so the preview pipeline stays aligned with the legacy boot.
 *
 * Key differences from the legacy boot:
 * - Blocks are replaced atomically via `CONTENT_STORE_LOCAL_ACTIONS.setBlocks`
 *   (not `fetchBlocksSuccess`) to avoid the triple NgRx effect (NG0103).
 * - CB1 default pages/blocks are NOT injected. CB2 fallback routes
 *   (`WEBAPP_CB2_DEFAULT_PAGE_ROUTES`) are merged the same way as production.
 *   System modals still come from DEFAULT_MODALS.
 * - Re-applying an identical payload is a no-op (deduped by JSON signature) to
 *   prevent redundant emissions / ExpressionChanged errors.
 */
@Injectable({ providedIn: 'root' })
export class Cb2PreviewMarketplaceApplierService {
  private readonly store = inject(Store);
  private readonly previewState = inject(Cb2PreviewStateService);
  private readonly contentDispatcher = inject(ContentStoreDispatcher);
  private readonly marketplaceDispatcher = inject(MarketplaceStoreDispatcher);
  private readonly router = inject(Router);
  private readonly localizeRouterService = inject(LocalizeRouterService);
  private readonly routerHelperService = inject(RouterHelperService);
  private readonly shopSearchParametersService = inject(ShopSearchParametersService);
  private readonly orderTypeService = inject(OrderTypeService);
  private readonly shopStoreSelector = inject(ShopStoreSelector);
  private readonly journeyParametersRuntime = inject(JourneyParametersRuntimeService);
  private readonly cb2ContextualRuntimeResolver = inject(Cb2ContextualRuntimeResolverService);

  private lastAppliedPreviewKey: string | null = null;

  /**
   * Last successfully staged payload. Kept so a viewport change can re-resolve
   * `$cases` (device branches) without waiting for the next soft-refresh.
   */
  private lastPayload: Cb2PreviewRuntimePayload | null = null;

  /**
   * Signature of the last projected `pages` tree. `createMarketplaceRoutes`
   * pushes marketplace routes into `router.config[0].children` without dedup
   * (and `parser.load` does NOT reset the router config), so re-calling it on
   * every soft-refresh would accumulate duplicate page routes — and Angular
   * activates sibling empty-path routes, rendering the preview page multiple
   * times. We only (re)create routes when the pages structure actually changes.
   */
  private lastAppliedPagesKey: string | null = null;

  apply(payload: Cb2PreviewRuntimePayload): DoodMarketplaceModel | null {
    const stub = payload.marketplace;
    if (!stub?.id) {
      return null;
    }

    this.lastPayload = payload;

    const previewScenarios = this.resolvePreviewScenarios(payload);
    // Seed locale from the stub before resolving `$cases`: at iframe boot the
    // bridge has not called `setLocale` yet, and falling back to `fr` freezes
    // the wrong branch when the subsequent identical payload is deduped.
    const previewLocale = this.resolvePreviewLocale(payload);
    if (!this.previewState.locale()) {
      this.previewState.setLocale(previewLocale);
    }
    const previewDevice = this.previewState.viewportPreset() ?? 'desktop';

    const signature = JSON.stringify({
      payload,
      locale: previewLocale,
      device: previewDevice,
    });
    if (signature === this.lastAppliedPreviewKey) {
      // Still push scenario-only updates — tab sync can change preview state
      // without mutating blocks/modals/marketplace in the payload body.
      this.previewState.setPreviewScenarios(previewScenarios);
      return null;
    }
    this.lastAppliedPreviewKey = signature;

    this.previewState.setPreviewScenarios(previewScenarios);
    this.journeyParametersRuntime.setParamValues(payload.paramValues);

    const blocks = this.resolveBlocks(payload, previewLocale, previewDevice);
    const merged: DoodMarketplaceModel = {
      ...(stub as unknown as DoodMarketplaceModel),
      pages: (payload.pages ?? stub.pages) as DoodMarketplaceModel['pages'],
      blocks,
      modals: (payload.modals ?? stub.modals) as DoodMarketplaceModel['modals'],
      user_interface:
        (payload.user_interface as IMarketplaceUserInterface | undefined) ??
        (stub as unknown as { user_interface?: IMarketplaceUserInterface }).user_interface,
    };

    // Align with CB2 production boot: keep journey pages/blocks as-is, but
    // always merge platform system modals (e.g. kiosk-suggestions-modal).
    const withDefaults = injectDefaultModals(merged);
    const withStableModalIds = stabilizeInjectedPreviewModalBlockIds(
      withDefaults,
      this.journeyModalHandles(payload),
    );
    const withPageTree = injectCb2DefaultPages(convertToPageTree(withStableModalIds));
    this.marketplaceDispatcher.updateMarketplace(withPageTree);
    // Atomic block replacement — bypasses the fetchBlocksSuccess effect chain.
    this.contentDispatcher.setModals(withPageTree.modals ?? []);
    this.store.dispatch(
      CONTENT_STORE_LOCAL_ACTIONS.setBlocks({
        blocks: (withPageTree.blocks ?? []) as DoodBlockModel[],
      }),
    );

    const rootBlockId = (blocks[0] as { id?: string } | undefined)?.id ?? null;
    this.previewState.setRootBlockId(rootBlockId);

    applyMarketplaceUserInterface(withPageTree);
    if (withPageTree.pages && withPageTree.pages.length > 0) {
      const pagesSignature = JSON.stringify(withPageTree.pages);
      if (pagesSignature !== this.lastAppliedPagesKey) {
        this.lastAppliedPagesKey = pagesSignature;
        createMarketplaceRoutes(withPageTree, {
          router: this.router,
          localizeRouterService: this.localizeRouterService,
          routerHelperService: this.routerHelperService,
        });
      }
    } else {
      this.lastAppliedPagesKey = null;
    }
    setMarketplaceHtmlLang(withPageTree);
    setMarketplaceDefaultTimezone(withPageTree);
    initializeDefaultDistributionMode(withPageTree, {
      shopSearchParametersService: this.shopSearchParametersService,
      orderTypeService: this.orderTypeService,
    });

    const authMode = resolveAuthMode(withPageTree);
    if (authMode) {
      this.marketplaceDispatcher.updateMarketplace({ auth_mode: authMode });
    }

    return withPageTree;
  }

  /**
   * Re-resolves the last preview payload against the current locale/viewport.
   * Used when the studio changes the preview device so `$cases` device branches
   * (e.g. Frame `layout` on mobile) update without a draft mutation.
   */
  reapplyLastPayload(): DoodMarketplaceModel | null {
    if (!this.lastPayload) {
      return null;
    }
    return this.apply(this.lastPayload);
  }

  private journeyModalHandles(payload: Cb2PreviewRuntimePayload): ReadonlySet<string> {
    const modals = (payload.modals ?? []) as Array<{ handle?: string }>;
    return new Set(
      modals.map(modal => modal.handle).filter((handle): handle is string => !!handle),
    );
  }

  private resolvePreviewScenarios(
    payload: Cb2PreviewRuntimePayload,
  ): Readonly<Record<string, Cb2PreviewScenarioPayload>> {
    if (payload.previewScenarios && Object.keys(payload.previewScenarios).length > 0) {
      return payload.previewScenarios;
    }

    if (payload.previewScenario) {
      return { __legacy__: payload.previewScenario };
    }

    return {};
  }

  private resolvePreviewLocale(payload: Cb2PreviewRuntimePayload): string {
    const fromState = this.previewState.locale()?.trim();
    if (fromState) {
      return fromState;
    }

    const fromStub = payload.marketplace?.default_locale?.locale?.trim();
    if (fromStub) {
      return fromStub;
    }

    return 'fr';
  }

  private resolveBlocks(
    payload: Cb2PreviewRuntimePayload,
    locale: string,
    device: string,
  ): IBlockConfig[] {
    const activeShop = this.shopStoreSelector.selectActive();
    const rawShopId = activeShop?.[ShopKeys.Id];
    const shopId = typeof rawShopId === 'string' && rawShopId.trim() ? rawShopId.trim() : null;

    return this.cb2ContextualRuntimeResolver.stageCompiledRuntime(
      {
        blocks: (payload.blocks ?? []) as IBlockConfig[],
        paramValues: payload.paramValues,
        paramCatalog: payload.paramCatalog,
      },
      {
        locale,
        device,
        shopId,
      },
    );
  }

  private readAuthProviderSnapshot(
    payload: Cb2PreviewRuntimePayload,
  ): Record<string, unknown> | null {
    const modals = (payload.modals ?? []) as Array<{
      handle?: string;
      data?: Record<string, unknown>;
    }>;
    const authModal = modals.find(modal => modal.handle === 'authentication-modal');
    if (!authModal?.data) {
      return null;
    }

    const data = authModal.data;
    return {
      google: data['authenticationProviderGoogle'] ?? data['authentication_provider_google'],
      facebook: data['authenticationProviderFacebook'] ?? data['authentication_provider_facebook'],
      apple: data['authenticationProviderApple'] ?? data['authentication_provider_apple'],
    };
  }

  private readAuthProviderSnapshotFromBlocks(
    payload: Cb2PreviewRuntimePayload,
  ): Record<string, unknown> | null {
    const blocks = (payload.blocks ?? []) as Array<{
      type?: string;
      data?: Record<string, unknown>;
    }>;
    const authBlock = blocks.find(block => block.type === 'AuthenticationModalComponent');
    if (!authBlock?.data) {
      return null;
    }

    const data = authBlock.data;
    return {
      google: data['authenticationProviderGoogle'] ?? data['authentication_provider_google'],
      facebook: data['authenticationProviderFacebook'] ?? data['authentication_provider_facebook'],
      apple: data['authenticationProviderApple'] ?? data['authentication_provider_apple'],
    };
  }
}
